import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Icons, PageTitle } from '@components/common';
import Table from '@components/common/Table/Table';

import { useQueryPortalProjects } from '@hooks/portal/projects/portalProjects/useQueryPortalProjects';
import { useQueryPortalProjectStatuses } from '@hooks/portal/projects/portalProjects/useQueryPortalProjectsStatuses';

import { portalProjects } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import { resetPortalProjects, setPage, setPageSize, setSearchValue, setSort, setStatus } from '@slices/portalProjects';

import { setFilterUtil } from '@utils/filterUtil';

import { TSelectOption } from '@interfaces/components/componentTypes';
import { ISortBy, ITablePagination, ITableSearch, ITableSort, TTableFilter } from '@interfaces/components/table';

import { EAdminPanelPageTitles } from '@enums/common';

import { searchTooltipText } from './constants';

import { getColumns } from './manageProjectsColumns';

import styles from './Projects.module.scss';

const Projects = () => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();

	const [selectedProjectStatus, setSelectedProjectStatus] = useState<TSelectOption<string>>(null);

	const {
		page,
		pageSize,
		searchValue,
		status,
		sortDirection,
		sortFieldName,
	} = useAppSelector(portalProjects);

	const { portalProjectsList, isLoadingPortalProjects, countOfRecords } = useQueryPortalProjects();

	const { portalProjectsStatuses, isLoadingPortalProjectsStatuses } = useQueryPortalProjectStatuses();

	const columns = useMemo(() => getColumns({ navigate }), [navigate]);

	const onSearchHandler = useCallback((searchValue: string) => dispatch(setSearchValue({ searchValue })), [dispatch]);

	const search: Array<ITableSearch> = useMemo(() => [
		{
			initialValue: searchValue,
			name: 'searchValue',
			handler: onSearchHandler,
			tooltipText: searchTooltipText,
			placeholder: searchTooltipText,
		},
	], [searchValue, onSearchHandler]);

	const handleSort = useCallback((sortBy: Array<ISortBy>) => {
		dispatch(setSort({
			sortDirection: !!sortBy[0]?.desc,
			sortFieldName: sortBy[0]?.id,
		}));
	}, [dispatch]);

	const sort: ITableSort = useMemo(() => (
		{
			initialState: [{ id: sortFieldName, desc: sortDirection }],
			handleSort,
		}
	), [sortFieldName, sortDirection, handleSort]);

	const pageHandler = useCallback((page: number) => dispatch(setPage({ page })), [dispatch]);

	const pageSizeHandler = useCallback((pageSize: number) => dispatch(setPageSize({ pageSize })), [dispatch]);

	const pagination: ITablePagination = useMemo(() => (
		{
			countOfRecords,
			forcePage: page,
			size: pageSize,
			pageHandler,
			pageSizeHandler,
		}
	), [
		countOfRecords,
		page,
		pageSize,
		pageHandler,
		pageSizeHandler,
	]);

	const filters: Array<TTableFilter> = useMemo(() => {
		return [
			{
				name: 'status',
				placeholder: 'Status',
				option: selectedProjectStatus,
				options: portalProjectsStatuses,
				minWidth: '200px',
				setState: setSelectedProjectStatus,
				defaultValue: status,
			},
		];
	}, [portalProjectsStatuses, selectedProjectStatus, status]);

	useEffect(() => {
		setFilterUtil({
			selectedValue: selectedProjectStatus,
			currentValue: status,
			setAction: setStatus,
			fieldName: 'status',
			dispatch,
		});
	}, [dispatch, status, selectedProjectStatus]);

	const isLoading = isLoadingPortalProjects || isLoadingPortalProjectsStatuses;

	useEffect(() => {
		return () => {
			dispatch(resetPortalProjects());
		};
	}, [dispatch]);

	return (
		<div className={styles.projectsContainer}>
			<PageTitle
				icon={Icons.portalIcon()}
				title={EAdminPanelPageTitles.Projects}
			/>
			<div className={styles.projectsTableContainer}>
				<Table
					columns={columns}
					data={portalProjectsList}
					filters={filters}
					isLoading={isLoading}
					isPinColumns
					isWithoutCheckbox
					isWithoutDeleteAction
					pagination={pagination}
					search={search}
					searchThreshold={0}
					sort={sort}
					stickyLeftColumnIndexes={[0, 1]}
				/>
			</div>
		</div>
	);
};

export default Projects;
